import React from 'react';
import { Box, Paper, Typography, Divider, Tooltip, IconButton } from '@mui/material';
import { Help } from '@mui/icons-material';

interface StepSectionProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  stepNumber?: number;
  children: React.ReactNode;
  completed?: boolean;
  active?: boolean;
  helpText?: string;
  sx?: object;
}

const StepSection: React.FC<StepSectionProps> = ({
  title,
  subtitle,
  icon,
  stepNumber,
  children,
  completed = false,
  active = false,
  helpText,
  sx = {}
}) => {
  return (
    <Paper 
      elevation={active ? 3 : 1} 
      sx={{ 
        mb: 4, 
        borderRadius: 3, 
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        boxShadow: active 
          ? '0 8px 24px rgba(25, 118, 210, 0.15)' 
          : '0 2px 8px rgba(0, 0, 0, 0.06)',
        border: active 
          ? '2px solid rgba(25, 118, 210, 0.2)' 
          : '1px solid rgba(0, 0, 0, 0.08)',
        opacity: completed && !active ? 0.85 : 1,
        ...sx
      }}
    >
      {/* Header Section */}
      <Box sx={{ 
        p: 3, 
        backgroundColor: active 
          ? 'rgba(25, 118, 210, 0.04)' 
          : completed 
            ? 'rgba(76, 175, 80, 0.04)' 
            : 'rgba(245, 245, 245, 0.6)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          {stepNumber && (
            <Box sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: active 
                ? 'primary.main' 
                : completed 
                  ? 'success.main' 
                  : 'grey.400',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '1.1rem',
              mr: 2,
              transition: 'all 0.3s ease'
            }}>
              {stepNumber}
            </Box>
          )}
          
          {icon && (
            <Box sx={{ 
              mr: 2,
              color: active ? 'primary.main' : completed ? 'success.main' : 'text.secondary',
              fontSize: 28,
              display: 'flex',
              alignItems: 'center'
            }}>
              {icon}
            </Box>
          )}
          
          <Box>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600,
                color: active ? 'primary.main' : completed ? 'success.main' : 'text.primary',
                letterSpacing: '-0.01em',
                mb: subtitle ? 0.5 : 0
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  fontWeight: 400
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        
        {helpText && (
          <Tooltip title={helpText} arrow placement="left">
            <IconButton 
              size="small" 
              sx={{ 
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              <Help fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      
      {/* Content Section */}
      <Box sx={{ p: 3 }}>
        {children}
      </Box>
    </Paper>
  );
};

export default StepSection;